import { useCallback, useEffect, useRef, useState } from "react";
import * as Speech from "expo-speech";
import {
  ExpoSpeechRecognitionModule,
  useSpeechRecognitionEvent,
} from "expo-speech-recognition";

import { trpc } from "../trpc";
import { friendlyTrpcError } from "../trpc-error";

// The voice-coach state machine. Drives the full on-device loop:
//
//   listening (on-device STT) → thinking (voice.coach LLM) → speaking
//   (on-device TTS) → listening → …
//
// STT is expo-speech-recognition (native SFSpeechRecognizer / Android, and
// the Web Speech API on web); TTS is expo-speech (native OS voices / Web
// Speech Synthesis). No external STT/TTS service — the only network hop is
// the LLM turn. Recognition runs one utterance at a time (continuous:false):
// on each final transcript we stop, send it to the coach, speak the reply,
// then re-open the mic — a natural turn-based conversation.

export type VoiceCoachState =
  | "idle"
  | "listening"
  | "thinking"
  | "speaking"
  | "paused"
  | "denied"
  | "unsupported"
  | "error";

interface UseVoiceCoachArgs {
  verticalSlug: string | null;
  jurisdictionCode: string | null;
}

interface UseVoiceCoachReturn {
  state: VoiceCoachState;
  /** The coach's most recent spoken reply (shown while speaking). */
  coachText: string;
  /** The live transcript of what the user is currently saying. */
  userText: string;
  error: string | null;
  /** True while the session is running (mic on or mid-turn) — drives the bar. */
  active: boolean;
  /** True only while the mic is actively capturing — drives the orb pulse. */
  capturing: boolean;
  start: () => void;
  end: () => void;
  togglePause: () => void;
}

const TTS_OPTIONS = { language: "en-US" } as const;

export function useVoiceCoach({
  verticalSlug,
  jurisdictionCode,
}: UseVoiceCoachArgs): UseVoiceCoachReturn {
  const [state, setState] = useState<VoiceCoachState>("idle");
  const [coachText, setCoachText] = useState("");
  const [userText, setUserText] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Conversation continuity + control flags read inside event listeners
  // (refs avoid stale closures — the listeners are registered once).
  const sessionIdRef = useRef<string | undefined>(undefined);
  const runningRef = useRef(false);
  const pausedRef = useRef(false);
  const finalTranscriptRef = useRef("");

  const beginListening = useCallback(() => {
    finalTranscriptRef.current = "";
    setUserText("");
    setState("listening");
    try {
      ExpoSpeechRecognitionModule.start({
        lang: "en-US",
        interimResults: true,
        continuous: false,
      });
    } catch {
      // start() can throw synchronously if the recognizer is busy; surface
      // as an error rather than hanging in "listening".
      setError("Couldn't start the microphone. Try again.");
      setState("error");
      runningRef.current = false;
    }
  }, []);

  const askCoach = useCallback(
    async (transcript: string) => {
      if (!verticalSlug) return;
      setState("thinking");
      try {
        const res = await trpc.voice.coach.mutate({
          verticalSlug,
          jurisdictionCode: jurisdictionCode ?? undefined,
          userMessage: transcript,
          voiceSessionId: sessionIdRef.current,
        });
        sessionIdRef.current = res.voiceSessionId;
        setCoachText(res.reply);
        if (!runningRef.current) {
          setState("idle");
          return;
        }
        setState("speaking");
        Speech.speak(res.reply, {
          ...TTS_OPTIONS,
          onDone: () => {
            if (runningRef.current && !pausedRef.current) beginListening();
            else setState(pausedRef.current ? "paused" : "idle");
          },
          onStopped: () => {
            if (pausedRef.current) setState("paused");
          },
          onError: () => {
            // TTS failed — don't strand the turn; re-open the mic.
            if (runningRef.current && !pausedRef.current) beginListening();
          },
        });
      } catch (err) {
        setError(friendlyTrpcError(err));
        setState("error");
        runningRef.current = false;
      }
    },
    [verticalSlug, jurisdictionCode, beginListening],
  );

  // ---- Recognition events -------------------------------------------------
  useSpeechRecognitionEvent("result", (event) => {
    const transcript = event.results?.[0]?.transcript ?? "";
    setUserText(transcript);
    if (event.isFinal) finalTranscriptRef.current = transcript;
  });

  useSpeechRecognitionEvent("end", () => {
    if (!runningRef.current || pausedRef.current) return;
    const transcript = finalTranscriptRef.current.trim();
    finalTranscriptRef.current = "";
    // Nothing intelligible captured — quietly re-open the mic.
    if (transcript.length === 0) {
      beginListening();
      return;
    }
    void askCoach(transcript);
  });

  useSpeechRecognitionEvent("error", (event) => {
    // "no-speech" just means the user stayed quiet — keep listening rather
    // than erroring out of the session.
    if (event.error === "no-speech") {
      if (runningRef.current && !pausedRef.current) beginListening();
      return;
    }
    if (event.error === "aborted") return; // our own stop/abort
    setError("The coach lost the audio connection. Tap the mic to resume.");
    pausedRef.current = true;
    setState("paused");
  });

  // ---- Controls -----------------------------------------------------------
  const start = useCallback(() => {
    setError(null);
    if (!ExpoSpeechRecognitionModule.isRecognitionAvailable()) {
      setState("unsupported");
      return;
    }
    void (async () => {
      try {
        const perm = await ExpoSpeechRecognitionModule.requestPermissionsAsync();
        if (!perm.granted) {
          setState("denied");
          return;
        }
        runningRef.current = true;
        pausedRef.current = false;
        beginListening();
      } catch {
        setState("denied");
      }
    })();
  }, [beginListening]);

  const end = useCallback(() => {
    runningRef.current = false;
    pausedRef.current = false;
    try {
      ExpoSpeechRecognitionModule.abort();
    } catch {
      // already stopped
    }
    Speech.stop();
    setState("idle");
  }, []);

  const togglePause = useCallback(() => {
    if (pausedRef.current) {
      // Resume.
      pausedRef.current = false;
      runningRef.current = true;
      setError(null);
      beginListening();
      return;
    }
    // Pause: silence the mic + any in-flight speech, keep the session/context.
    pausedRef.current = true;
    try {
      ExpoSpeechRecognitionModule.abort();
    } catch {
      // already stopped
    }
    Speech.stop();
    setState("paused");
  }, [beginListening]);

  // Tear down on unmount so the mic/TTS never outlive the screen.
  useEffect(() => {
    return () => {
      runningRef.current = false;
      pausedRef.current = false;
      try {
        ExpoSpeechRecognitionModule.abort();
      } catch {
        // already stopped
      }
      Speech.stop();
    };
  }, []);

  return {
    state,
    coachText,
    userText,
    error,
    active: state !== "idle" && state !== "denied" && state !== "unsupported",
    capturing: state === "listening",
    start,
    end,
    togglePause,
  };
}
